import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Button,
  FiShare2,
  Flex,
  Heading,
  HTMLWrapper,
  SocialShare,
  Tag,
  useBreakpointValue
} from "@/storybook-components";
import { toSentenceCase } from "@/helpers/to-sentence-case";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { signPrivateEmbedHtml } from "@/helpers/sign-private-embed-html";

function About({ closeSideBar }: any) {
  const { query } = useRouter();
  const { translate } = useTranslator();

  const { projectId, brainstormerId } = query;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { name, description, tagList, layout, toolConfigurationAttributes } =
    useAppSelector((state) => state.brainstormers.brainstormer);
  const { disableSocialMediaSharing } = useAppSelector(
    (state) => state.settings.settings
  );
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const signedDescription = signPrivateEmbedHtml(description, token);

  const isMobile = useBreakpointValue({ base: true, lg: false }) || false;
  const listingHeader =
    toolConfigurationAttributes?.preferences?.listingHeader || "";

  return (
    <Flex direction="column" gap={6} flex={1} data-testid="ideas-about">
      <Heading
        as="h1"
        fontWeight="bold"
        fontSize="2xl"
        color="gray.900"
        data-testid="ideas-about-heading"
      >
        {name}
      </Heading>
      <HTMLWrapper
        isFullWidth
        fontSize="sm"
        color="gray.800"
        html={signedDescription ?? description}
      />

      {(tagList?.length > 0 ||
        !disableSocialMediaSharing ||
        layout !== "BudgetAllocator") && (
        <Flex direction="column" gap={4}>
          {tagList?.length > 0 && (
            <Flex gap={2} wrap="wrap">
              {tagList.map((tag: string) => (
                <Tag
                  key={tag}
                  fontSize="sm"
                  variant="solid"
                  bgColor="white"
                  color="gray.800"
                  style={{ direction: "rtl" }}
                >
                  {tag}
                </Tag>
              ))}
            </Flex>
          )}
          {!disableSocialMediaSharing && layout !== "BudgetAllocator" && (
            <Box>
              <SocialShare
                title={name}
                placement="auto"
                data-testid="ideas-about-share"
                shareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}`}
                trigger={
                  <Button
                    px={3}
                    w="auto"
                    size="lg"
                    variant="tertiary"
                    bgColor="transparent"
                    leftIcon={<FiShare2 />}
                  >
                    {translate("common.share")}
                  </Button>
                }
              />
            </Box>
          )}
        </Flex>
      )}
      {isMobile && layout !== "BudgetAllocator" && (
        <Button
          variant="primary"
          mt="auto"
          size="lg"
          w="full"
          onClick={closeSideBar}
          data-testid="ideas-about-view-listings"
        >
          {toSentenceCase(
            translate("tools.viewListings", {
              listingHeader:
                listingHeader.trim() || translate("tools.ideas.listingHeader")
            })
          )}
        </Button>
      )}
    </Flex>
  );
}

export default About;
